import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchCustomers, saveCustomer, sendBillEmail } from '../api';

// ── Indian number-to-words ────────────────────────────────────────────────────
function toWords(n) {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
    'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function c(x) {
    if (!x) return '';
    if (x < 20)       return ones[x];
    if (x < 100)      return tens[Math.floor(x/10)] + (x%10 ? ' '+ones[x%10] : '');
    if (x < 1000)     return ones[Math.floor(x/100)] + ' Hundred' + (x%100 ? ' '+c(x%100) : '');
    if (x < 100000)   return c(Math.floor(x/1000)) + ' Thousand' + (x%1000 ? ' '+c(x%1000) : '');
    if (x < 10000000) return c(Math.floor(x/100000)) + ' Lakh' + (x%100000 ? ' '+c(x%100000) : '');
    return c(Math.floor(x/10000000)) + ' Crore' + (x%10000000 ? ' '+c(x%10000000) : '');
  }
  return (c(Math.round(n)) || 'Zero') + ' Rupees Only';
}

// ── Rich HTML email bill (table-based for email client compatibility) ─────────
function buildBillHtml(order, business) {
  const dt = new Date(order.created_at).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const total   = parseFloat(order.total_amount);
  const gstPct  = parseFloat(business.gst_percent || 0);
  const gstAmt  = gstPct > 0 ? +(total * gstPct / (100 + gstPct)).toFixed(2) : 0;
  const baseAmt = gstPct > 0 ? +(total - gstAmt).toFixed(2) : total;

  const itemRows = order.items.map((item, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#fafafa';
    return `
    <tr>
      <td style="background:${bg};padding:11px 14px;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;width:30px">${i + 1}</td>
      <td style="background:${bg};padding:11px 14px;font-size:14px;color:#1e293b;font-weight:500;border-bottom:1px solid #f1f5f9">${item.name}</td>
      <td style="background:${bg};padding:11px 14px;font-size:13px;color:#475569;text-align:center;border-bottom:1px solid #f1f5f9;width:50px">${item.quantity}</td>
      <td style="background:${bg};padding:11px 14px;font-size:13px;color:#475569;text-align:right;border-bottom:1px solid #f1f5f9;width:80px">&#8377;${parseFloat(item.price).toFixed(2)}</td>
      <td style="background:${bg};padding:11px 14px;font-size:14px;color:#1e293b;font-weight:600;text-align:right;border-bottom:1px solid #f1f5f9;width:90px">&#8377;${(item.price * item.quantity).toFixed(2)}</td>
    </tr>`;
  }).join('');

  const gstRows = gstPct > 0 ? `
    <tr>
      <td colspan="4" style="padding:8px 14px;text-align:right;font-size:13px;color:#64748b;background:#f8fafc">Base Amount</td>
      <td style="padding:8px 14px;text-align:right;font-size:13px;color:#475569;background:#f8fafc">&#8377;${baseAmt.toFixed(2)}</td>
    </tr>
    <tr>
      <td colspan="4" style="padding:8px 14px;text-align:right;font-size:13px;color:#64748b;background:#f8fafc">GST @${gstPct}% (included)</td>
      <td style="padding:8px 14px;text-align:right;font-size:13px;color:#475569;background:#f8fafc">&#8377;${gstAmt.toFixed(2)}</td>
    </tr>` : '';

  const orderTypeBadge = order.order_type && order.order_type !== 'dine-in'
    ? `<span style="display:inline-block;background:#fff3e0;color:#e65100;font-size:11px;
                    font-weight:700;padding:2px 10px;border-radius:20px;text-transform:capitalize;
                    margin-left:8px">${order.order_type}</span>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Invoice #${order.id} from ${business.name}</title>
</head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,'Helvetica Neue',Helvetica,sans-serif">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:32px 0">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.10)">

  <!-- ── HEADER ── -->
  <tr>
    <td style="background:linear-gradient(135deg,#FF6B35 0%,#f7522b 100%);padding:32px 32px 28px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td>
            <div style="font-size:26px;font-weight:800;color:#ffffff;letter-spacing:-0.5px;line-height:1.1">${business.name}</div>
            ${business.description ? `<div style="color:rgba(255,255,255,0.8);font-size:13px;margin-top:4px">${business.description}</div>` : ''}
            <div style="margin-top:14px;font-size:12px;color:rgba(255,255,255,0.85);line-height:1.9">
              ${business.address ? `<span>&#128205; ${business.address}</span><br>` : ''}
              ${business.phone   ? `<span>&#128222; ${business.phone}</span>` : ''}
              ${business.phone && business.email ? '&nbsp;&nbsp;|&nbsp;&nbsp;' : ''}
              ${business.email   ? `<span>&#9993; ${business.email}</span>` : ''}
            </div>
          </td>
          <td align="right" valign="top">
            <div style="background:rgba(255,255,255,0.22);border:2px solid rgba(255,255,255,0.5);
                        border-radius:6px;padding:6px 14px;display:inline-block">
              <div style="color:#fff;font-size:10px;font-weight:700;letter-spacing:2px">TAX INVOICE</div>
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <!-- ── INVOICE META ── -->
  <tr>
    <td style="background:#fff7f4;border-bottom:2px solid #ffe4d6;padding:20px 32px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td>
            <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Invoice No.</div>
            <div style="font-size:22px;font-weight:800;color:#FF6B35">#${order.id}</div>
            <div style="font-size:12px;color:#64748b;margin-top:4px">${dt}</div>
          </td>
          <td align="right">
            <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Bill To</div>
            <div style="font-size:17px;font-weight:700;color:#1e293b">${order.customer_name || 'Customer'}${orderTypeBadge}</div>
            ${order.customer_email ? `<div style="font-size:12px;color:#64748b;margin-top:3px">&#9993; ${order.customer_email}</div>` : ''}
            ${order.table_no ? `<div style="font-size:12px;color:#64748b;margin-top:3px">&#128205; ${order.table_no}</div>` : ''}
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <!-- ── ITEMS TABLE HEADER ── -->
  <tr>
    <td style="padding:0 32px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:24px">
        <thead>
          <tr style="background:#1e293b">
            <th style="padding:12px 14px;text-align:left;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:30px">#</th>
            <th style="padding:12px 14px;text-align:left;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px">ITEM</th>
            <th style="padding:12px 14px;text-align:center;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:50px">QTY</th>
            <th style="padding:12px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:80px">RATE</th>
            <th style="padding:12px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:90px">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
          ${gstRows}
          <!-- Total row -->
          <tr>
            <td colspan="4" style="padding:16px 14px;text-align:right;font-size:14px;font-weight:700;color:#1e293b;background:#fff7f4;border-top:2px solid #ffe4d6">
              TOTAL AMOUNT
            </td>
            <td style="padding:16px 14px;text-align:right;font-size:20px;font-weight:800;color:#FF6B35;background:#fff7f4;border-top:2px solid #ffe4d6;white-space:nowrap">
              &#8377;${total.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>

  <!-- ── AMOUNT IN WORDS ── -->
  <tr>
    <td style="padding:16px 32px 8px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px 18px">
            <span style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:.8px">Total in words:&nbsp;</span>
            <span style="font-size:13px;font-weight:600;color:#374151;font-style:italic">${toWords(total)}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  ${order.notes ? `
  <!-- ── NOTES ── -->
  <tr>
    <td style="padding:8px 32px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="background:#fefce8;border:1px solid #fde047;border-radius:8px;padding:12px 16px;font-size:13px;color:#713f12">
            <strong>Note:</strong> ${order.notes}
          </td>
        </tr>
      </table>
    </td>
  </tr>` : ''}

  <!-- ── FOOTER ── -->
  <tr>
    <td style="padding:24px 32px 28px">
      <table width="100%" cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="border-top:1px solid #e2e8f0;padding-top:20px;text-align:center">
            <div style="font-size:15px;font-weight:700;color:#1e293b;margin-bottom:4px">
              Thank you for your purchase! &#127881;
            </div>
            <div style="font-size:12px;color:#94a3b8">
              We hope to see you again soon.
            </div>
            <div style="margin-top:14px">
              <span style="font-size:11px;color:#cbd5e1">Powered by&nbsp;</span>
              <span style="font-size:12px;font-weight:700;color:#FF6B35">Scanningo.in</span>
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>

</table>
</td></tr>
</table>
</body>
</html>`;
}

// ── BillModal ─────────────────────────────────────────────────────────────────
export default function BillModal({ order, business, onClose }) {
  const [custEmail,   setCustEmail]   = useState(order.customer_email || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop,    setShowDrop]    = useState(false);
  const [remember,    setRemember]    = useState(!!order.customer_email);
  const [activeTab,   setActiveTab]   = useState('preview');
  const [sending,     setSending]     = useState(false);
  const [sent,        setSent]        = useState(false);  // success state
  const [sendErr,     setSendErr]     = useState('');
  const [emailDisabled, setEmailDisabled] = useState(false);
  const iframeRef = useRef(null);
  const dropRef   = useRef(null);

  const billHtml = buildBillHtml(order, business);

  useEffect(() => {
    const frame = iframeRef.current;
    if (frame) frame.srcdoc = billHtml;
  }, [billHtml]);

  // Customer autocomplete
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); setShowDrop(false); return; }
    try {
      const res = await searchCustomers(q);
      setSuggestions(res);
      setShowDrop(res.length > 0);
    } catch { setSuggestions([]); setShowDrop(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(custEmail), 300);
    return () => clearTimeout(t);
  }, [custEmail, doSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectSuggestion = (c) => {
    setCustEmail(c.email || '');
    setSuggestions([]); setShowDrop(false);
  };

  const printBill = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(billHtml);
    win.document.close();
    win.onload = () => win.print();
  };

  // Direct send — no clipboard, no paste, fully automatic
  const handleSend = async () => {
    if (!custEmail.trim()) { setSendErr('Please enter the customer email.'); return; }
    setSending(true); setSendErr('');

    if (remember) {
      try { await saveCustomer({ name: order.customer_name || '', email: custEmail, phone: order.table_no || '' }); }
      catch {}
    }

    const subject = `Your Bill from ${business.name} — Invoice #${order.id}`;
    try {
      await sendBillEmail(custEmail.trim(), subject, billHtml, business.email || '');
      setSent(true);
    } catch (e) {
      if (e.message === 'EMAIL_NOT_CONFIGURED' || e.message?.includes('EMAIL_NOT_CONFIGURED')) {
        setEmailDisabled(true);
        setSendErr('__not_configured__');
      } else {
        setSendErr(e.message || 'Could not send email. Please try again.');
      }
    }
    setSending(false);
  };

  const tabBtn = (id, label) => (
    <button onClick={() => { setActiveTab(id); setSent(false); setSendErr(''); setEmailDisabled(false); }}
      style={{
        padding: '8px 20px', border: 'none', cursor: 'pointer', fontWeight: 600,
        fontSize: 13, borderRadius: '8px 8px 0 0',
        background: activeTab === id ? 'rgba(255,255,255,0.08)' : 'transparent',
        color: activeTab === id ? '#f1f5f9' : '#64748b',
        borderBottom: activeTab === id ? '2px solid #FF6B35' : '2px solid transparent',
      }}>{label}</button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{
        background: '#1e2535', border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 16, width: '100%', maxWidth: 700,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '18px 22px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#f1f5f9' }}>Send Bill — Order #{order.id}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {order.customer_name || 'Guest'} · ₹{order.total_amount}
              {order.customer_email && <span style={{ color: '#6366f1', marginLeft: 8 }}>✉ {order.customer_email}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#94a3b8', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 18,
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ padding: '0 22px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {tabBtn('preview', '👁  Preview')}
          {tabBtn('send', '✉️  Send Bill')}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 22px' }}>

          {/* ── Preview tab ── */}
          {activeTab === 'preview' && (
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                This is exactly how the email will look in the customer's inbox.
              </div>
              <iframe ref={iframeRef} title="Bill Preview" style={{
                width: '100%', height: 520, border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10, background: '#f1f5f9',
              }} />
              <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <button onClick={printBill} style={{
                  padding: '9px 20px', background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8,
                  fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#e2e8f0',
                }}>🖨 Print</button>
                <button onClick={() => setActiveTab('send')} style={{
                  flex: 1, padding: '9px 20px', background: '#FF6B35', color: '#fff',
                  border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>✉️ Send to Customer →</button>
              </div>
            </div>
          )}

          {/* ── Send tab ── */}
          {activeTab === 'send' && (
            <div>

              {/* ── SUCCESS STATE ── */}
              {sent && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#34d399', marginBottom: 8 }}>
                    Bill Sent Successfully!
                  </div>
                  <div style={{ fontSize: 14, color: '#6ee7b7', marginBottom: 24 }}>
                    The formatted bill was delivered to<br />
                    <b style={{ color: '#a7f3d0', fontSize: 16 }}>{custEmail}</b>
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button onClick={() => { setSent(false); setCustEmail(''); }}
                      style={{ padding: '10px 24px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 9, color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>
                      Send to another
                    </button>
                    <button onClick={onClose}
                      style={{ padding: '10px 24px', background: '#10b981', border: 'none', borderRadius: 9, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                      Done ✓
                    </button>
                  </div>
                </div>
              )}

              {/* ── NOT CONFIGURED / ERROR STATE ── */}
              {!sent && emailDisabled && (
                <div>
                  <div style={{
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 12, padding: '20px', marginBottom: 20,
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#f87171', marginBottom: 8 }}>⚠️ Email could not be sent</div>
                    <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>
                      There was a problem delivering the email. Please try again or print the bill instead.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setEmailDisabled(false)} style={{
                      flex: 1, padding: '12px', background: '#FF6B35', border: 'none',
                      borderRadius: 9, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}>↩ Try Again</button>
                    <button onClick={printBill} style={{
                      flex: 1, padding: '12px', background: '#4338ca', border: 'none',
                      borderRadius: 9, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    }}>🖨 Print Bill</button>
                  </div>
                </div>
              )}

              {/* ── NORMAL SEND FORM ── */}
              {!sent && !emailDisabled && (
                <div>
                  {/* Customer email */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 12, color: '#94a3b8', display: 'block', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                      Customer Email *
                    </label>
                    <div style={{ position: 'relative' }} ref={dropRef}>
                      <input
                        type="email"
                        value={custEmail}
                        onChange={(e) => { setCustEmail(e.target.value); setSendErr(''); }}
                        placeholder="customer@gmail.com"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                        style={{
                          width: '100%', padding: '13px 16px',
                          background: 'rgba(255,255,255,0.06)', border: `1px solid ${sendErr && !custEmail ? '#ef4444' : 'rgba(255,255,255,0.14)'}`,
                          borderRadius: 9, fontSize: 15, color: '#e2e8f0', outline: 'none',
                        }}
                        onFocus={() => { if (suggestions.length > 0) setShowDrop(true); }}
                        autoFocus
                      />
                      {showDrop && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                          background: '#1a2332', border: '1px solid rgba(255,255,255,0.14)',
                          borderRadius: '0 0 10px 10px', maxHeight: 180, overflow: 'auto',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        }}>
                          {suggestions.map((c) => (
                            <div key={c.id} onClick={() => selectSuggestion(c)} style={{
                              padding: '11px 14px', cursor: 'pointer', fontSize: 13,
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                            }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,107,53,0.1)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = ''}>
                              <span style={{ color: '#f1f5f9', fontWeight: 600 }}>{c.name || c.email}</span>
                              {c.email && <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>{c.email}</span>}
                              {c.order_count > 0 && <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 11 }}>{c.order_count} orders</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {order.customer_email && order.customer_email !== custEmail && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 5 }}>
                        Customer provided: <b style={{ color: '#94a3b8' }}>{order.customer_email}</b>
                        <button onClick={() => setCustEmail(order.customer_email)} style={{
                          marginLeft: 8, fontSize: 11, color: '#FF6B35', background: 'none',
                          border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600,
                        }}>← Use this</button>
                      </div>
                    )}
                  </div>

                  {/* Remember customer */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22, cursor: 'pointer' }}>
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: '#FF6B35' }} />
                    <span style={{ fontSize: 13, color: '#94a3b8' }}>Remember this customer for future orders</span>
                  </label>

                  {/* Error */}
                  {sendErr && sendErr !== '__not_configured__' && (
                    <div style={{
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                      borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                      fontSize: 13, color: '#f87171',
                    }}>{sendErr}</div>
                  )}

                  {/* SEND BUTTON — big, prominent, one click */}
                  <button onClick={handleSend} disabled={sending} style={{
                    width: '100%', padding: '16px 20px',
                    background: sending ? 'rgba(255,107,53,0.5)' : 'linear-gradient(135deg,#FF6B35,#f7522b)',
                    border: 'none', borderRadius: 11, color: '#fff',
                    fontWeight: 800, fontSize: 16, cursor: sending ? 'not-allowed' : 'pointer',
                    boxShadow: sending ? 'none' : '0 6px 20px rgba(255,107,53,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    marginBottom: 12, transition: 'all .2s',
                  }}>
                    {sending
                      ? <><span style={{ fontSize: 20 }}>⏳</span> Sending Bill…</>
                      : <><span style={{ fontSize: 22 }}>📨</span> Send Bill to Customer</>
                    }
                  </button>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={printBill} style={{
                      flex: 1, padding: '9px 14px',
                      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 9, color: '#64748b', fontSize: 13, cursor: 'pointer',
                    }}>🖨 Print instead</button>
                  </div>

                  <div style={{ marginTop: 16, fontSize: 11, color: '#334155', textAlign: 'center' }}>
                    Bill is sent from <b>bills@scanningo.in</b> directly to the customer's inbox
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
